import identity from 'lodash/identity'
import { GetCurrentUserWorkspaceMemberships } from 'iam/WorkspaceMembershipStore'
import { GetCurrentMember } from 'iam/SessionStore'
import { computed, makeObservable } from 'mobx'
import once from 'lodash/once'
import { AlertFactory } from 'alerts/Alert'
import { FetchUserTeamMembers, FilterTeamMembers } from 'iam/TeamMemberStore'
import { task } from 'mobx-task'
import { FilterStorageFactory } from 'infra/FiltersViewModel'
import { matchesWaitingOnFilter } from './JobWaitingOnFilterOption'
import { Storage } from 'infra/storage'
import { FindLegends } from 'legends/PublishedLegendStore'
import { JobsLoadFilterItemsAdapter } from 'jobs/infra/JobsLoadFilterItemsAdapter'
import JobSearchViewModel, {
  TicketsAdapter,
} from 'jobs/infra/JobSearchViewModel'
import JobSearchAdapter from 'jobs/infra/JobSearchAdapter'
import JobsTableColumnManager from 'jobs/infra/JobsTableColumnManager'
import { first } from 'lodash'
import {
  MutableColumnDetails,
  StaticColumnDetails,
} from 'jobs/infra/JobsTableColumns'
import { TableSortOrder } from 'components/Table'
import UserDataStore from 'iam/UserDataStore'
import { JobTicketStore } from '../../tickets/JobTicketStore'
import SearchableJob from '../../SearchableJob'
import { getFeatureToggleClient } from 'infra/featureToggles'
import { FindUserTeams } from '../../../iam/TeamStore'
import { AutomatedAction } from '../../../opportunities/offers/OpportunityOffersViewModel'
import { ProviderActivePtinStatusDto } from '../../../iam/models/ProviderActivePtinStatusDto'
import { UserDataType } from '../../../iam/models/UserDataType'

/**
 * Job Management view model.
 */
export default class JobManagementViewModel {
  /**
   * Adapter to load filter items.
   */
  loadFilterItemsAdapter = new JobsLoadFilterItemsAdapter(
    this.getCurrentUserWorkspaceMemberships,
    this.findLegends,
    this.findUserTeams
  )

  /**
   * Adapter for retrieving tickets.
   */
  ticketsAdapter: TicketsAdapter = {
    fetchLatestOpenTicketsForJobs:
      this.jobTicketStore.fetchLatestOpenTicketsForJobs,
  }

  /**
   * Job Searcher.
   */
  searcher = new JobSearchViewModel(
    this.jobFetcher,
    this.loadFilterItemsAdapter,
    this.ticketsAdapter,
    this.newAlert,
    // Don't apply client-side filter inside the searcher.
    // Otherwise we would end up showing a Completely Empty State
    // when there may very well be jobs in the search result.
    identity,
    this.getCurrentMember,
    this.userDataStore,
    this.getCurrentUserWorkspaceMemberships,
    this.filterTeamMembers,
    this.createFilterStorage('JobManagement')
  )

  /**
   * Ensure we only activate the searcher once, as we want to keep it around
   * for subsequent page views of job management.
   */
  private activateSearcher = once(() => this.searcher.activate())

  /**
   * Column manager.
   */
  columnManager = new JobsTableColumnManager(
    this.localStorage,
    this.searcher.getAllFilteredJobs,
    this.getCurrentMember,
    this.getCurrentUserWorkspaceMemberships,
    this.newAlert,
    this.userDataStore
  )

  /**
   * Constructor.
   */
  constructor(
    private jobFetcher: JobSearchAdapter,
    private newAlert: AlertFactory,
    private findLegends: FindLegends,
    private getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMemberships,
    private fetchUserTeamMembers: FetchUserTeamMembers,
    private getCurrentMember: GetCurrentMember,
    private canDraft: () => boolean,
    private jobTicketStore: JobTicketStore,
    private createFilterStorage: FilterStorageFactory,
    private localStorage: Storage,
    private userDataStore: UserDataStore,
    private filterTeamMembers: FilterTeamMembers,
    private _canEditDraftJobAsAdmin: () => boolean,
    private findUserTeams: FindUserTeams,
    private _useEditDraftJobForFirmAdmin: () => boolean,
    private _useJobCreationDialog: boolean,
    public handleOfferResponse: (action: AutomatedAction) => Promise<void>
  ) {
    makeObservable(this)
  }

  /**
   * Computes whether an active PTIN is required for the current member.
   *
   * @returns {boolean} Returns true if an active PTIN is required, otherwise false.
   */
  @computed
  get ptinRequired(): boolean {
    const featureEnabled = getFeatureToggleClient().variation(
      'Worklayer.RequireActivePTIN'
    )
    if (!featureEnabled) {
      return false
    }

    const userData = this.userDataStore.getByPublicId(
      this.currentMember.user.publicId
    )

    return (
      userData?.userDataType === UserDataType.Pro &&
      userData?.ptinStatus === ProviderActivePtinStatusDto.Inactive
    )
  }

  /**
   * Only display jobs where the current user is a collaborator
   * and match the filters.
   */
  @computed
  get filteredJobs() {
    return this.searcher.jobs.filter((job) =>
      matchesWaitingOnFilter(
        job.waitingOn,
        first(this.searcher.filters.appliedFilters?.waitingOn)
      )
    )
  }

  /**
   * Whether we are showing the empty job view
   */
  @computed
  get showEmptyView() {
    return (
      this.filteredJobs.length === 0 &&
      !this.searcher.scroller.fetch.pending &&
      this.searcher.scroller.isDone
    )
  }

  /**
   * Total count for the search result (does not account for client-side filtering which we should get rid of when we can)
   */
  @computed
  get totalSearchResultCount() {
    return this.searcher.lastSearch.total
  }

  /**
   * Current user workspaces.
   */
  @computed
  get currentUserWorkspaces() {
    return this.getCurrentUserWorkspaceMemberships().map((m) => m.workspace)
  }

  /**
   * Current user memberships.
   */
  @computed
  get currentUserMembers() {
    return this.getCurrentUserWorkspaceMemberships().map((m) => m.member)
  }

  /**
   * Getter for the current member.
   */
  @computed
  get currentMember() {
    return this.getCurrentMember()
  }

  /**
   * Which tip to display.
   */
  @computed
  get tip(): Tip {
    const member = this.currentMember
    if (member.hasPermission('VIEW_OPPORTUNITIES')) {
      return Tip.DiscoverOpportunities
    }

    if (this.canDraft()) {
      return Tip.GetWorkDone
    }

    return Tip.None
  }

  /**
   * Whether the user can edit draft job as admin.
   */
  @computed
  get canEditDraftJobAsAdmin() {
    return (
      this._canEditDraftJobAsAdmin() &&
      getFeatureToggleClient().variation(
        'WorkPortal.UseEditDraftJobAsAdmin',
        false
      )
    )
  }

  /**
   * Whether the current member is a firm admin
   */
  @computed
  get useEditDraftJobForFirmAdmin() {
    return this._useEditDraftJobForFirmAdmin()
  }

  /**
   * Whether all users should be using the job creation dialog.
   */
  @computed
  get useJobCreationDialog() {
    return this._useJobCreationDialog
  }

  /**
   * Gets the sort order for the specified column.
   *
   * @param column
   * @returns
   */
  getSortOrder(
    column: StaticColumnDetails & MutableColumnDetails
  ): TableSortOrder {
    return this.searcher.sorting.type === 'Field' &&
      this.searcher.sorting.field === column.field
      ? this.searcher.sorting.direction
      : 'none'
  }

  /**
   * Sets the sort order for the specified column.
   *
   * @param column
   * @param direction
   * @returns
   */
  setSortOrder(
    column: StaticColumnDetails & MutableColumnDetails,
    direction: TableSortOrder
  ) {
    if (!column.field) {
      return
    }

    this.searcher.setSortField(column.field)
    this.searcher.setSortDirection(direction)
  }

  /**
   * Gets the latest open ticket for the specified job.
   */
  getLatestOpenTicketForJob(job: SearchableJob) {
    return this.jobTicketStore.getLatestTicketForJob(job.id)
  }

  /**
   * Activates the view model.
   */
  activate = task.resolved(async () => {
    await this.fetchUserTeamMembers()
    this.activateSearcher()
    this.columnManager.activate()
  })

  /**
   * Deactivates the view model.
   */
  deactivate() {
    // We don't deactivate the searcher because we want
    // to keep the state for the searcher around.
  }
}

/**
 * Which tip to display.
 */
export const enum Tip {
  None,
  DiscoverOpportunities,
  GetWorkDone,
}
